import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  bulkDeleteIndirectTaxTransactions,
  bulkExportIndirectTaxTransactions,
  commitIndirectTaxTransaction,
  lockIndirectTaxTransaction,
  useIndirectTaxTransactions,
  useIndirectTaxTransactionAggregates,
  useIndirectTaxTransactionFilterOptions,
  voidIndirectTaxTransaction,
} from '@commenda/api/indirect-tax/transactions';
import {
  TIndirectTaxBulkActionTransactionsParams,
  TIndirectTaxTransactionBulkActionFilters,
  TIndirectTaxMarketplace,
  TIndirectTaxSortState,
  TIndirectTaxTransactionExportDeliveryType,
  TIndirectTaxTransactionStatus as ApiTransactionStatus,
  TIndirectTaxTransactionType as ApiTransactionType,
} from '@commenda/api/indirect-tax/transactions/types';
import { JobEntity } from '@commenda/api';
import { getClientInfoFromCookie } from '@commenda/auth';
import { useToggle } from '@commenda/kakashi';

import { useDebounce } from '../../../hooks/useDebounce';
import { useNotification } from '../../../hooks/useNotification';
import { getIntegrationSyncState } from '../../../sales-tax/common/integrationDataSync/utils';
import { useSalesTaxRootFiConnectFlow } from '../../../sales-tax/common/useSalesTaxRootFiConnectFlow';
import { useIndirectTaxIntegrationSyncProgressStatus } from '../../settings/hooks/useIndirectTaxIntegrationSyncProgressStatus';
import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { INDIRECT_TAX_TABLE_IDS } from '../../common/constants/tableIds';
import { useRowSelection } from '../../common/hooks/useRowSelection';
import { useIndirectTaxJobWatcher } from '../../common/hooks/useIndirectTaxJobWatcher';
import { useIndirectTaxCSVJobWatcher } from '../../common/hooks/useIndirectTaxCSVJobWatcher';
import {
  buildKey,
  useRegionScopedSessionState,
  useSessionPersistedState,
} from '../../common/utils/filterStorage';
import { IndirectTaxTransactionHelper, IndirectTaxTransactionMapper } from '../helpers';
import { TIndirectTaxTransaction, TIndirectTaxTransactionStatus, TIndirectTaxTransactionTableFilters } from '../types';
import { buildIndirectTaxTransactionApiFilters } from './buildTransactionApiFilters';

const PAGE_LIMIT = 100;

// Seed state for the transactions dashboard when it's opened in a preset
// context (e.g. the filing "View transactions" modal prefilling the
// reporting-date and destination filters). Filters without a matching region
// drift from the active tab the user sees, so the two are always supplied
// together or not at all.
export interface IndirectTaxTransactionsDashboardInitialState {
  filters: Record<string, string | string[]>;
  region: IndirectTaxRegion;
}

interface UseIndirectTaxTransactionsDashboardParams {
  corporationId: number;
  initialState?: IndirectTaxTransactionsDashboardInitialState;
}

export function useIndirectTaxTransactionsDashboard({
  corporationId,
  initialState,
}: UseIndirectTaxTransactionsDashboardParams) {
  // When invoked with an externally provided `initialState` (e.g. the filing
  // "View transactions" modal), the hook runs ephemerally: defaults come from
  // `initialState`, not sessionStorage, and filter/region/search/sort changes
  // the user makes inside that session are NOT written back to sessionStorage.
  // This keeps modal sessions fully isolated from the main /transactions
  // page's corporation-level persisted filters.
  const shouldPersistFilters = initialState == null;
  const persistedKeyForSlot = (
    slot: Parameters<typeof buildKey>[1]
  ): string | null =>
    shouldPersistFilters
      ? buildKey(INDIRECT_TAX_TABLE_IDS.TRANSACTIONS, slot, corporationId)
      : null;

  const [activeRegion, setActiveRegion] =
    useSessionPersistedState<IndirectTaxRegion>(
      persistedKeyForSlot('region'),
      initialState?.region ?? IndirectTaxRegion.ALL_REGIONS
    );


  const [activeFilters, setActiveFilters] = useRegionScopedSessionState<
    Record<string, string | string[]>
  >(
    persistedKeyForSlot('filters'),
    activeRegion,
    initialState ? { [initialState.region]: initialState.filters } : undefined
  );

  const [searchInput, setSearchInput] = useSessionPersistedState<string>(
    persistedKeyForSlot('search'),
    ''
  );
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  const [sortState, setSortState] =
    useSessionPersistedState<TIndirectTaxSortState>(
      persistedKeyForSlot('sort'),
      { id: 'date', desc: true }
    );

  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const {
    data: filterOptionsData,
    isLoading: isLoadingFilterOptions,
    error: filterOptionsError,
  } = useIndirectTaxTransactionFilterOptions(corporationId);

  const regionCountries =
    IndirectTaxRegionHelper.getCountriesForRegion(activeRegion);

  const apiFilters = useMemo(
    () =>
      buildIndirectTaxTransactionApiFilters({
        corporationId,
        region: activeRegion,
        filters: activeFilters,
        searchTerm: debouncedSearchTerm,
      }),
    [corporationId, debouncedSearchTerm, activeFilters, activeRegion]
  );

  const {
    data: transactionsData,
    mutate: mutateTransactions,
    error: transactionsError,
    isLoading: isLoadingTransactions,
  } = useIndirectTaxTransactions({
    filters: apiFilters,
    cursor: currentCursor,
    limit: PAGE_LIMIT,
    sortState,
  });

  // Region tab drives the aggregate currency. The active region maps to its
  // default display currency (US/ALL_REGIONS -> USD, EU -> EUR, UK -> GBP,
  // CA -> CAD). List + detail responses still come back in invoice_currency
  // per row — only the aggregate is converted.
  const aggregateTargetCurrency =
    IndirectTaxTransactionHelper.getDefaultCurrencyForRegion(activeRegion);

  const {
    aggregates,
    isLoading: isLoadingAggregates,
    error: aggregatesError,
    mutate: mutateAggregates,
  } = useIndirectTaxTransactionAggregates({
    filters: { ...apiFilters, target_currency: aggregateTargetCurrency },
  });

  const { isJobInProgress, getJobsByEntityAndOperation, refreshJobs } =
    useIndirectTaxJobWatcher({
      corporation_id: corporationId,
    });

  const {
    isCSVProcessingInProgress,
    refreshJobs: refreshCsvJobs,
  } = useIndirectTaxCSVJobWatcher({ corporationId });
  
  const isTransactionJobInProgress =
  isJobInProgress(JobEntity.INVOICE) || isCSVProcessingInProgress();

  const prevJobInProgressRef = useRef(false);

  useEffect(() => {
    if (prevJobInProgressRef.current && !isTransactionJobInProgress) {
      mutateTransactions();
      mutateAggregates();
    }
    prevJobInProgressRef.current = isTransactionJobInProgress;
  }, [isTransactionJobInProgress, mutateTransactions, mutateAggregates]);

  const { setNotification } = useNotification();
  const {
    integrations,
    handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
  } = useSalesTaxRootFiConnectFlow({
    corporationId,
    setNotification,
    onRootFiConnectSuccess: () => mutateTransactions(),
  });

  const { isIntegrationSyncRecordDataProcessing } =
    useIndirectTaxIntegrationSyncProgressStatus(corporationId);

  const isIntegrationSyncInProgress =
    getIntegrationSyncState(integrations ?? []) ||
    isIntegrationSyncRecordDataProcessing();

  const mappedTransactions = useMemo(
    () =>
      (transactionsData.transactions ?? []).map((item) =>
        IndirectTaxTransactionMapper.mapListItemToTransaction(item, activeRegion)
      ),
    [transactionsData.transactions, activeRegion]
  );

  const transactions = mappedTransactions;

  const summaryConfigs = useMemo(
    () => IndirectTaxTransactionHelper.SUMMARY_CONFIGS[activeRegion],
    [activeRegion]
  );

  const hasInitialDataRef = useRef(false);
  if (transactionsData.totalTransactions > 0 || mappedTransactions.length > 0) {
    hasInitialDataRef.current = true;
  }

  const hasTransactions =
    hasInitialDataRef.current || transactionsData.totalTransactions > 0 || mappedTransactions.length > 0;

  const importTransactionsModal = useToggle();

  const resetCursor = useCallback(() => {
    setCurrentCursor(null);
    setCursorHistory([]);
  }, []);

  const handleNextPage = useCallback(() => {
    if (transactionsData.nextCursor) {
      setCursorHistory((prev) => [
        ...prev,
        currentCursor ?? '__first__',
      ]);
      setCurrentCursor(transactionsData.nextCursor);
    }
  }, [transactionsData.nextCursor, currentCursor]);

  const handlePreviousPage = useCallback(() => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCurrentCursor(
        prevCursor === '__first__' ? null : prevCursor ?? null
      );
      return next;
    });
  }, []);

  const transactionSelection = useRowSelection({
    getItemId: (item: TIndirectTaxTransaction): string => item.id,
    totalItems: transactionsData.totalTransactions,
  });

  const handleRegionChange = useCallback(
    (region: IndirectTaxRegion) => {
      setActiveRegion(region);
      transactionSelection.resetSelection();
      resetCursor();
    },
    [resetCursor, transactionSelection]
  );

  const handleFilterChange = useCallback(
    (filters: Record<string, string | string[]>) => {
      setActiveFilters(filters);
      transactionSelection.resetSelection();
      resetCursor();
    },
    [resetCursor, transactionSelection]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      transactionSelection.resetSelection();
      resetCursor();
    },
    [setSearchInput, transactionSelection, resetCursor]
  );

  const handleSortChange = useCallback(
    (newSortState: TIndirectTaxSortState) => {
      setSortState(newSortState);
      resetCursor();
    },
    [resetCursor]
  );

  const bulkDeleteModal = useToggle();

  const handleRowAction = useCallback(
    async (
      transaction: TIndirectTaxTransaction,
      action: TIndirectTaxTransactionStatus
    ) => {
      let result: { success: boolean; error?: string };

      if (action === TIndirectTaxTransactionStatus.COMMITTED) {
        result = await commitIndirectTaxTransaction(transaction.id, corporationId);
      } else if (action === TIndirectTaxTransactionStatus.LOCKED) {
        result = await lockIndirectTaxTransaction(transaction.id, corporationId);
      } else if (action === TIndirectTaxTransactionStatus.VOIDED) {
        result = await voidIndirectTaxTransaction(transaction.id, corporationId);
      } else {
        return { success: false, error: 'Unknown action' };
      }

      if (result.success) {
        mutateTransactions();
      }
      return result;
    },
    [corporationId, mutateTransactions]
  );

  const buildBulkActionFilters = useCallback((): TIndirectTaxTransactionBulkActionFilters => {
    const filters: TIndirectTaxTransactionBulkActionFilters = {
      from_date: '1970-01-01',
      to_date: new Date().toISOString().split('T')[0],
    };

    const periodFilter = activeFilters[TIndirectTaxTransactionTableFilters.PERIOD];
    if (periodFilter) {
      const { fromDate, toDate } = IndirectTaxTransactionHelper.parseDateFilterValue(String(periodFilter));
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
    }

    const countryFilter = activeFilters[TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES];
    const selectedCountries = countryFilter
      ? (Array.isArray(countryFilter) ? countryFilter : [countryFilter])
      : regionCountries;

    const stateFilter = activeFilters[TIndirectTaxTransactionTableFilters.DESTINATION_STATES];
    const stateAddresses = stateFilter
      ? (Array.isArray(stateFilter) ? stateFilter : [stateFilter]).map((s) => {
          const [country, state] = s.split('_');
          return {
            country,
            states: [state],
          };
        })
      : [];
    const countriesWithSelectedStates = new Set(
      stateAddresses.map((address) => address.country)
    );

    if (selectedCountries && selectedCountries.length > 0) {
      filters.ship_to_addresses = selectedCountries
        .filter((country) => !countriesWithSelectedStates.has(country))
        .map((country) => ({ country }));
    }

    if (stateAddresses.length > 0) {
      filters.ship_to_addresses = [
        ...(filters.ship_to_addresses ?? []),
        ...stateAddresses,
      ];
    }

    const statusFilter = activeFilters[TIndirectTaxTransactionTableFilters.STATUS];
    if (statusFilter) {
      const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
      // Safe: status filter options are typed ApiTransactionStatus enum values
      if (statuses.length > 0) filters.statuses = statuses as ApiTransactionStatus[];
    }

    const allowedTransactionTypes = IndirectTaxTransactionHelper.getTransactionTypesForRegion(activeRegion);
    const typeFilter = activeFilters[TIndirectTaxTransactionTableFilters.TRANSACTION_TYPE];
    const selectedTypes = (typeFilter ? (Array.isArray(typeFilter) ? typeFilter : [typeFilter]) : [])
      .filter((t) => allowedTransactionTypes.includes(t as ApiTransactionType)) as ApiTransactionType[];
    const effectiveTypes = selectedTypes.length > 0 ? selectedTypes : allowedTransactionTypes;
    if (effectiveTypes.length < Object.keys(ApiTransactionType).length) {
      filters.types = effectiveTypes;
    }

    const sourcePlatformFilter = activeFilters[TIndirectTaxTransactionTableFilters.SOURCE_PLATFORM];
    if (sourcePlatformFilter) {
      const platforms = Array.isArray(sourcePlatformFilter) ? sourcePlatformFilter : [sourcePlatformFilter];
      // Safe: source platform values match the union type from TIndirectTaxTransactionBulkActionFilters
      if (platforms.length > 0) filters.source_platforms = platforms as TIndirectTaxTransactionBulkActionFilters['source_platforms'];
    }

    const marketplaceFilter = activeFilters[TIndirectTaxTransactionTableFilters.MARKETPLACE];
    if (marketplaceFilter) {
      const marketplaces = Array.isArray(marketplaceFilter) ? marketplaceFilter : [marketplaceFilter];
      // Safe: marketplace filter options are typed TIndirectTaxMarketplace enum values
      if (marketplaces.length > 0) filters.marketplaces = marketplaces as TIndirectTaxMarketplace[];
    }

    const originCountryFilter = activeFilters[TIndirectTaxTransactionTableFilters.ORIGIN_COUNTRIES];
    if (originCountryFilter) {
      const origins = Array.isArray(originCountryFilter) ? originCountryFilter : [originCountryFilter];
      if (origins.length > 0) {
        filters.ship_from_addresses = origins.map((country) => ({ country }));
      }
    }

    if (debouncedSearchTerm) {
      filters.search_term = debouncedSearchTerm;
    }

    return filters;
  }, [activeFilters, regionCountries, debouncedSearchTerm, activeRegion]);

  const buildBulkActionParams = useCallback((): TIndirectTaxBulkActionTransactionsParams => {
    const selectedIds = Array.from(transactionSelection.selectedItemIds);
    const excludedIds = Array.from(transactionSelection.unselectedItemIds);

    if (selectedIds.length > 0) {
      return {
        corporation_id: corporationId,
        transaction_ids: selectedIds,
      };
    }

    return {
      corporation_id: corporationId,
      excluded_ids: excludedIds,
      filters: buildBulkActionFilters(),
    };
  }, [corporationId, transactionSelection, buildBulkActionFilters]);

  const handleBulkDelete = useCallback(async () => {
    const params = buildBulkActionParams();
    const response = await bulkDeleteIndirectTaxTransactions(params);

    if (response.success) {
      mutateTransactions();
      transactionSelection.resetSelection();
      resetCursor();
      return { success: true };
    }
    return { success: false, error: response.error };
  }, [buildBulkActionParams, mutateTransactions, transactionSelection, resetCursor]);

  const handleBulkExport = useCallback(async () => {
    const clientInfo = getClientInfoFromCookie();
    const user = clientInfo?.user;
    const params = buildBulkActionParams();

    const response = await bulkExportIndirectTaxTransactions({
      ...params,
      delivery: {
        type: TIndirectTaxTransactionExportDeliveryType.EMAIL,
        recipients: user?.email ? [user.email] : [],
      },
    });

    if (response.success) {
      transactionSelection.resetSelection();
      return { success: true };
    }
    return { success: false, error: response.error };
  }, [buildBulkActionParams, transactionSelection]);

  const isInitialLoading =
    (isLoadingTransactions || isLoadingFilterOptions) && !hasTransactions;

  return {
    activeRegion,
    transactions,
    mappedTransactions,
    transactionsData,
    summaryConfigs,
    hasTransactions,
    aggregates,
    activeFilters,
    filterOptionsData,
    searchInput,
    sortState,
    cursorHistory,
    handleNextPage,
    handlePreviousPage,
    isLoadingTransactions,
    isInitialLoading,
    transactionsError,
    isTransactionJobInProgress,
    isIntegrationSyncInProgress,
    handleRegionChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handleConnectIntegration: handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
    importTransactionsModal,
    getJobsByEntityAndOperation,
    transactionSelection,
    bulkDeleteModal,
    handleRowAction,
    handleBulkDelete,
    handleBulkExport,
    mutateTransactions,
    refreshJobs,
    refreshCsvJobs,
  };
}
